import React, { useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { QuantityUnit, QuantityBinding } from '@popety-io/types'
import { Input } from '../../atoms/Input'
import { AdvancedDropdown } from '../AdvancedDropdown'
import {
  DropdownOption,
  DropdownSection,
} from '../AdvancedDropdown/AdvancedDropdown.types'
import {
  QuantityInputProps,
  RevenueBindingData,
  LandData,
} from './QuantityInput.types'
import {
  Container,
  InputWrapper,
  DropdownWrapper,
  BindingWrapper,
} from './QuantityInput.styles'

const DEFAULT_UNIT_LABELS: Record<QuantityUnit, string> = {
  'm²': 'm²',
  'm³': 'm³',
  m: 'm',
  pcs: 'pcs',
  kg: 'kg',
  t: 't',
  '%': '%',
  custom: 'Custom',
}

const UNIT_KEYS: QuantityUnit[] = [
  'm²',
  'm³',
  'm',
  'pcs',
  'kg',
  't',
  '%',
  'custom',
]

// =====================================================
// Binding value resolution
// =====================================================

const resolveBindingValue = (
  binding: QuantityBinding | null,
  landData?: LandData,
  revenueData?: RevenueBindingData
): number | null => {
  if (!binding || binding === 'manual' || binding === 'costs.selected_groups') {
    return null
  }

  // Land bindings
  if (binding.startsWith('land.') && landData) {
    switch (binding) {
      case 'land.area':
        return landData.area ?? null
      case 'land.std':
        return landData.std ?? null
      case 'land.building_total_area':
        return landData.building_total_area ?? null
      case 'land.building_ground_floor_area':
        return landData.building_ground_floor_area ?? null
      case 'land.building_volume':
        return landData.building_volume ?? null
    }
  }

  // Revenue bindings
  if (binding.startsWith('revenue.') && revenueData) {
    const key = binding.replace('revenue.', '') as keyof RevenueBindingData
    return revenueData[key] ?? null
  }

  return null
}

// =====================================================
// Binding option configuration
// =====================================================

interface BindingOptionConfig {
  id: QuantityBinding
  label: string
  unit: string
  section: string
}

const BINDING_OPTIONS: BindingOptionConfig[] = [
  // Manual (pinned first, own section without header)
  { id: 'manual', label: 'Manual', unit: '', section: 'manual' },
  // Project areas (m²)
  {
    id: 'revenue.total_area',
    label: 'Total construction area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.floor_area_416',
    label: 'Floor area GF (SIA 416)',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.residential_area',
    label: 'Residential area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.commercial_office_area',
    label: 'Office area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.commercial_retail_area',
    label: 'Retail area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.industrial_area',
    label: 'Industrial area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  {
    id: 'revenue.storage_cellar_area',
    label: 'Storage / cellar area',
    unit: 'm²',
    section: 'revenue_areas',
  },
  // Project volumes (m³) — resolved server-side from SIA storey heights
  {
    id: 'revenue.total_volume_m3',
    label: 'Total volume (GV)',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  {
    id: 'revenue.residential_volume_m3',
    label: 'Residential volume',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  {
    id: 'revenue.commercial_office_volume_m3',
    label: 'Office volume',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  {
    id: 'revenue.commercial_retail_volume_m3',
    label: 'Retail volume',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  {
    id: 'revenue.industrial_volume_m3',
    label: 'Industrial volume',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  {
    id: 'revenue.storage_cellar_volume_m3',
    label: 'Storage / cellar volume',
    unit: 'm³',
    section: 'revenue_volumes',
  },
  // Floor types (UG/EG/OG/DG) — basement volume = excavation quantity
  {
    id: 'revenue.basement_area',
    label: 'Basement area (UG)',
    unit: 'm²',
    section: 'revenue_floor_types',
  },
  {
    id: 'revenue.basement_volume_m3',
    label: 'Basement volume (UG)',
    unit: 'm³',
    section: 'revenue_floor_types',
  },
  {
    id: 'revenue.ground_floor_area',
    label: 'Ground floor area (EG)',
    unit: 'm²',
    section: 'revenue_floor_types',
  },
  {
    id: 'revenue.upper_floor_area',
    label: 'Upper floors area (OG)',
    unit: 'm²',
    section: 'revenue_floor_types',
  },
  {
    id: 'revenue.attic_area',
    label: 'Attic area (DG)',
    unit: 'm²',
    section: 'revenue_floor_types',
  },
  // Project counts (pcs)
  {
    id: 'revenue.total_units',
    label: 'Total units',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.residential_units',
    label: 'Residential units',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.parking_indoor',
    label: 'Parking indoor',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.parking_outdoor',
    label: 'Parking outdoor',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.parking_total',
    label: 'Parking total',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.storage_units',
    label: 'Storage units',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.building_count',
    label: 'Buildings',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  {
    id: 'revenue.floor_count',
    label: 'Floors',
    unit: 'pcs',
    section: 'revenue_counts',
  },
  // Project financial
  {
    id: 'revenue.total_revenue',
    label: 'Total revenue',
    unit: 'CHF',
    section: 'revenue_financial',
  },
  // Land
  { id: 'land.area', label: 'Land area', unit: 'm²', section: 'land' },
  { id: 'land.std', label: 'STD', unit: 'm²', section: 'land' },
  // Existing buildings
  {
    id: 'land.building_total_area',
    label: 'Total building area',
    unit: 'm²',
    section: 'existing_buildings',
  },
  {
    id: 'land.building_ground_floor_area',
    label: 'Ground floor area',
    unit: 'm²',
    section: 'existing_buildings',
  },
  {
    id: 'land.building_volume',
    label: 'Building volume',
    unit: 'm³',
    section: 'existing_buildings',
  },
  // Cost reference
  {
    id: 'costs.selected_groups',
    label: '% of CFC groups',
    unit: '%',
    section: 'cost',
  },
]

// Project quantities first (what cost rows bind to most), then land and
// existing buildings, % of CFC last since it switches the input mode.
const SECTION_ORDER = [
  'manual',
  'revenue_areas',
  'revenue_volumes',
  'revenue_floor_types',
  'revenue_counts',
  'revenue_financial',
  'land',
  'existing_buildings',
  'cost',
]

const SECTION_TITLES: Record<string, string> = {
  manual: '',
  revenue_areas: 'PROJECT AREAS',
  revenue_volumes: 'PROJECT VOLUMES',
  revenue_floor_types: 'FLOOR TYPES (UG/EG/OG/DG)',
  revenue_counts: 'PROJECT COUNTS',
  revenue_financial: 'PROJECT FINANCIAL',
  land: 'LAND',
  existing_buildings: 'EXISTING BUILDINGS',
  cost: 'COST REFERENCE',
}

/**
 * BKP code → most relevant bindings, pinned as a "Suggested" section when
 * the edited row carries a standard code (longest-prefix match). Static and
 * deterministic — same philosophy as the catalog defaults.
 */
const SUGGESTED_BINDINGS: Record<string, QuantityBinding[]> = {
  '0': ['land.area'],
  '1': ['land.area'],
  '11': ['land.building_volume'],
  '17': ['revenue.basement_area'],
  '20': ['revenue.basement_volume_m3', 'revenue.basement_area'],
  '21': [
    'revenue.floor_area_416',
    'revenue.total_volume_m3',
    'revenue.total_area',
  ],
  '22': [
    'revenue.floor_area_416',
    'revenue.total_volume_m3',
    'revenue.total_area',
  ],
  '224': ['revenue.attic_area', 'revenue.ground_floor_area'],
  '23': ['revenue.floor_area_416', 'revenue.total_area'],
  '24': [
    'revenue.floor_area_416',
    'revenue.total_volume_m3',
    'revenue.total_area',
  ],
  '25': [
    'revenue.floor_area_416',
    'revenue.residential_units',
    'revenue.total_area',
  ],
  '26': ['revenue.floor_count', 'revenue.residential_units'],
  '27': ['revenue.floor_area_416', 'revenue.total_area'],
  '28': ['revenue.floor_area_416', 'revenue.total_area'],
  '4': ['land.area'],
  '42': ['land.area'],
  '45': ['land.area'],
  '46': ['land.area'],
}

/** Longest-prefix lookup: '224' wins over '22' wins over '2'. */
const suggestionsForCode = (code?: string | null): QuantityBinding[] => {
  if (!code) return []
  for (let len = code.length; len > 0; len--) {
    const hit = SUGGESTED_BINDINGS[code.slice(0, len)]
    if (hit) return hit
  }
  return []
}

// =====================================================
// Custom option rendering with live value
// =====================================================

const formatValue = (
  value: number | null | undefined,
  unit: string
): string => {
  if (value == null) return '—'
  const formatted = value.toLocaleString('de-CH')
  return unit ? `${formatted} ${unit}` : formatted
}

const OptionRow = styled.div<{ $dimmed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
`

const OptionLabel = styled.span`
  font-size: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const UNIT_CHIP_COLORS: Record<string, { bg: string; fg: string }> = {
  'm²': { bg: '#e0f2fe', fg: '#0369a1' },
  'm³': { bg: '#ede9fe', fg: '#6d28d9' },
  pcs: { bg: '#fef3c7', fg: '#b45309' },
  CHF: { bg: '#fce7f3', fg: '#be185d' },
  '%': { bg: '#f3f4f6', fg: '#4b5563' },
}

const UnitChip = styled.span<{ $unit: string }>`
  flex: none;
  width: 30px;
  text-align: center;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 4px;
  margin-right: 8px;
  background: ${({ $unit }) => UNIT_CHIP_COLORS[$unit]?.bg ?? 'transparent'};
  color: ${({ $unit }) => UNIT_CHIP_COLORS[$unit]?.fg ?? 'inherit'};
`

const OptionValue = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
  margin-left: 8px;
`

// =====================================================
// Component
// =====================================================

export const QuantityInput: React.FC<QuantityInputProps> = ({
  quantity,
  costPerUnit,
  quantityUnit,
  quantityBinding,
  customUnit,
  onQuantityChange,
  onCostPerUnitChange,
  onUnitChange,
  onBindingChange,
  onCustomUnitChange,
  landData,
  revenueData,
  disabled = false,
  error = false,
  size = 'medium',
  className,
  unitOnly = false,
  showQuantity = false,
  excludeUnits,
  excludeBindings,
  labels,
  suggestedBkpCode,
}) => {
  const isManual =
    quantityBinding === 'manual' ||
    quantityBinding === 'costs.selected_groups' ||
    !quantityBinding

  const unitOptions = useMemo(() => {
    return UNIT_KEYS.filter((key) => !excludeUnits?.includes(key)).map(
      (key) => ({
        id: key,
        label: labels?.units?.[key] ?? DEFAULT_UNIT_LABELS[key],
        value: key,
      })
    )
  }, [labels?.units, excludeUnits])

  // Build grouped binding sections
  const bindingSections = useMemo(() => {
    const hasRevenue = revenueData != null

    const sections: DropdownSection[] = []

    // Pinned context suggestions for the row's BKP code. Ids are prefixed so
    // they don't collide with the same binding in its home section.
    const suggested = suggestionsForCode(suggestedBkpCode)
      .map((id) => BINDING_OPTIONS.find((opt) => opt.id === id))
      .filter((opt): opt is BindingOptionConfig => !!opt)
      .map((opt) => ({
        opt,
        value: resolveBindingValue(opt.id, landData, revenueData),
      }))
      .filter(({ value }) => value != null && value !== 0)
    if (suggested.length > 0) {
      sections.push({
        id: 'suggested',
        title: `${labels?.suggestedForBkp ?? 'Suggested for BKP'} ${suggestedBkpCode}`,
        options: suggested.map(({ opt, value }) => ({
          id: `suggested::${opt.id}`,
          label: labels?.bindings?.[opt.id] ?? opt.label,
          data: {
            value,
            unit: opt.unit,
            displayValue: formatValue(value, opt.unit),
          },
        })),
      })
    }

    for (const sectionId of SECTION_ORDER) {
      // Skip revenue sections if no revenue data
      if (sectionId.startsWith('revenue') && !hasRevenue) continue

      const title = labels?.sections?.[sectionId] ?? SECTION_TITLES[sectionId]
      const sectionOptions = BINDING_OPTIONS.filter(
        (opt) => opt.section === sectionId && !excludeBindings?.includes(opt.id)
      )

      if (sectionOptions.length === 0) continue

      const options: DropdownOption[] = sectionOptions.map((opt) => {
        const value = resolveBindingValue(opt.id, landData, revenueData)
        const displayValue =
          opt.id === 'manual'
            ? '—'
            : opt.id === 'costs.selected_groups'
              ? '%'
              : formatValue(value, opt.unit)

        return {
          id: opt.id,
          label: labels?.bindings?.[opt.id] ?? opt.label,
          data: { value, unit: opt.unit, displayValue },
        }
      })

      sections.push({
        id: sectionId,
        title,
        options,
      })
    }

    return sections
  }, [landData, revenueData, labels, suggestedBkpCode, excludeBindings])

  // Custom option renderer with value on the right + dimming
  const renderBindingOption = (option: DropdownOption) => {
    const value = option.data?.value
    const displayValue = option.data?.displayValue ?? '—'
    const isDimmed =
      option.id !== 'manual' &&
      option.id !== 'costs.selected_groups' &&
      (value === 0 || value == null)

    const unit = option.data?.unit as string | undefined

    return (
      <OptionRow $dimmed={isDimmed}>
        {unit ? <UnitChip $unit={unit}>{unit}</UnitChip> : null}
        <OptionLabel>{option.label}</OptionLabel>
        <OptionValue>{displayValue}</OptionValue>
      </OptionRow>
    )
  }

  // Resolve quantity from binding
  const resolvedQuantity = useMemo(() => {
    if (isManual) return quantity
    return (
      resolveBindingValue(quantityBinding, landData, revenueData) ?? quantity
    )
  }, [quantityBinding, landData, revenueData, quantity, isManual])

  // Auto-update quantity when binding changes
  useEffect(() => {
    if (!isManual && resolvedQuantity !== quantity) {
      onQuantityChange(resolvedQuantity)
    }
  }, [resolvedQuantity, isManual])

  const handleUnitChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      onUnitChange(value as QuantityUnit)
    }
  }

  const handleBindingChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      onBindingChange(value.replace('suggested::', '') as QuantityBinding)
    }
  }

  const handleCostPerUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      onCostPerUnitChange(null)
    } else {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        onCostPerUnitChange(numValue)
      }
    }
  }

  const displayCostPerUnit = costPerUnit ?? ''

  // For percentage items (unitOnly), show quantity as the value
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      onQuantityChange(null)
    } else {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        onQuantityChange(numValue)
      }
    }
  }

  return (
    <Container className={className}>
      {!unitOnly && (
        <BindingWrapper>
          <AdvancedDropdown
            value={quantityBinding || 'manual'}
            onChange={handleBindingChange}
            sections={bindingSections}
            renderOption={renderBindingOption}
            searchable
            searchPlaceholder={
              labels?.bindingSearchPlaceholder ?? 'Search a binding…'
            }
            filterFunction={(option, term) => {
              const q = term.toLowerCase()
              return (
                String(option.label).toLowerCase().includes(q) ||
                String(option.data?.unit ?? '')
                  .toLowerCase()
                  .includes(q)
              )
            }}
            placeholder={labels?.bindingPlaceholder ?? 'Binding'}
            disabled={disabled}
            error={error}
            size={size}
            usePortal
            dropdownMinWidth={280}
            maxHeight={420}
            noHeader={false}
          />
        </BindingWrapper>
      )}

      {showQuantity && !unitOnly && (
        <InputWrapper>
          <Input
            type="number"
            value={(isManual ? quantity : resolvedQuantity) ?? ''}
            onChange={handlePercentageChange}
            placeholder={labels?.quantityValuePlaceholder ?? 'Quantity'}
            disabled={disabled || !isManual}
            error={error}
            size={size}
            fullWidth
          />
        </InputWrapper>
      )}

      <DropdownWrapper>
        {quantityUnit === 'custom' && onCustomUnitChange ? (
          <Input
            type="text"
            value={customUnit || ''}
            onChange={(e) => onCustomUnitChange(e.target.value)}
            placeholder={labels?.unitPlaceholder ?? 'Unit'}
            disabled={disabled}
            error={error}
            size={size}
          />
        ) : (
          <AdvancedDropdown
            value={quantityUnit || ''}
            onChange={handleUnitChange}
            options={unitOptions}
            placeholder={labels?.unitPlaceholder ?? 'Unit'}
            disabled={disabled}
            error={error}
            size={size}
            usePortal
          />
        )}
      </DropdownWrapper>

      {unitOnly ? (
        <InputWrapper>
          <Input
            type="number"
            value={quantity ?? ''}
            onChange={handlePercentageChange}
            placeholder="%"
            disabled={disabled}
            error={error}
            size={size}
            fullWidth
          />
        </InputWrapper>
      ) : (
        <InputWrapper>
          <Input
            type="number"
            value={displayCostPerUnit}
            onChange={handleCostPerUnitChange}
            placeholder={labels?.quantityPlaceholder ?? 'CHF/unit'}
            disabled={disabled}
            error={error}
            size={size}
            fullWidth
          />
        </InputWrapper>
      )}
    </Container>
  )
}

QuantityInput.displayName = 'QuantityInput'
